import * as React from 'react';
import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { withTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import VendorDialog from './VendorDialog';
import { formatDate } from '../../utils';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import PaginationControls from '../shared/PaginationControls';
import {
  useListPreferences,
  SearchInput,
} from '../shared/ListFilters';
import DataTable from '../shared/DataTable';

const STORAGE_KEY = 'vendors_list_preferences';

const defaultPreferences = {
  sortField: 'name',
  sortDirection: 'asc',
  searchQuery: '',
  filters: {},
};

const MainVendorsView = ({ t }) => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Use localStorage-persisted preferences
  const {
    preferences,
    updatePreferences,
  } = useListPreferences(STORAGE_KEY, defaultPreferences);

  // Use server-side pagination hook
  const {
    data: vendors,
    loading,
    error,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
    searchQuery,
    setSearchQuery,
    sortField,
    sortDirection,
    handleSort,
    refetch,
  } = usePaginatedList('/api/vendors/', {
    initialFilters: {},
    defaultSortField: preferences.sortField,
    defaultSortDirection: preferences.sortDirection,
    defaultPageSize: 50,
    initialSearchQuery: preferences.searchQuery || '',
  });

  // Sync search query from localStorage preferences to hook
  React.useEffect(() => {
    setSearchQuery(preferences.searchQuery || '');
  }, [preferences.searchQuery, setSearchQuery]);

  const handleVendorCreated = useCallback(() => {
    refetch();
    setSnackbar({ open: true, message: t('vendors.created'), severity: 'success' });
  }, [refetch, t]);

  const handleVendorUpdated = useCallback(() => {
    refetch();
    setSnackbar({ open: true, message: t('vendors.updated'), severity: 'success' });
  }, [refetch, t]);

  const handleVendorDeleted = useCallback(() => {
    refetch();
    setSnackbar({ open: true, message: t('vendors.deleted'), severity: 'success' });
  }, [refetch, t]);

  const handleRowClick = useCallback((vendor) => {
    setSelectedVendor(vendor);
    setEditDialogOpen(true);
  }, []);

  // Deep link `/vendors?edit=<id>` (the order builder sends the user here to add an
  // order email): open that vendor's edit dialog once, then drop the parameter.
  const [searchParams, setSearchParams] = useSearchParams();
  const editParam = searchParams.get('edit');
  const handledEditRef = React.useRef(null);
  React.useEffect(() => {
    if (!editParam || handledEditRef.current === editParam) return;
    handledEditRef.current = editParam;
    axios
      .get(`/api/vendors/${editParam}/`)
      .then((response) => {
        setSelectedVendor(response.data);
        setEditDialogOpen(true);
      })
      .catch(() => {});
    setSearchParams({}, { replace: true });
  }, [editParam, setSearchParams]);

  // State for error snackbar
  const [showErrorSnackbar, setShowErrorSnackbar] = React.useState(false);
  const prevError = React.useRef(error);

  React.useEffect(() => {
    if (error && error !== prevError.current) {
      setShowErrorSnackbar(true);
    }
    prevError.current = error;
  }, [error]);

  return (
    <Box data-testid="vendors-page">
      {/* Error Snackbar */}
      <Snackbar
        open={showErrorSnackbar && !!error}
        autoHideDuration={6000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowErrorSnackbar(false)} severity="error" sx={{ width: '100%' }}>
          {t('Error')}: {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {t('vendors.title')}
        </Typography>
        <Button
          variant="contained"
          data-testid="add-vendor-button"
          onClick={() => {
            setSelectedVendor(null);
            setAddDialogOpen(true);
          }}
          startIcon={<AddIcon />}
          sx={{
            bgcolor: 'secondary.main',
            color: 'white',
            px: 2.5,
            py: 1.5,
            borderRadius: '10px',
            fontWeight: 500,
            fontSize: '14px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            boxShadow: '0 4px 12px rgba(49, 130, 206, 0.3)',
            '&:hover': {
              bgcolor: 'secondary.light',
              boxShadow: '0 6px 16px rgba(49, 130, 206, 0.4)',
            },
          }}
        >
          {t('vendors.addVendor')}
        </Button>
      </Box>

      {/* Search Row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 2.5,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <SearchInput
          value={preferences.searchQuery || ''}
          onChange={(value) => updatePreferences({ searchQuery: value })}
          onClear={() => updatePreferences({ searchQuery: '' })}
          placeholder={t('vendors.searchPlaceholder')}
        />
      </Box>

      {/* Desktop Table */}
      <DataTable
        columns={[
          {
            key: 'name',
            label: t('vendors.name'),
            sortable: true,
            render: (vendor) => (
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary' }}>
                {vendor.name}
              </Typography>
            ),
          },
          {
            key: 'business_id',
            label: t('Business ID'),
            sortable: true,
            render: (vendor) => (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {vendor.business_id || '-'}
              </Typography>
            ),
          },
          {
            key: 'contact_person',
            label: t('Contact Person'),
            render: (vendor) => (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {vendor.contact_person || '-'}
              </Typography>
            ),
          },
          {
            key: 'email',
            label: t('Email Address'),
            maxWidth: 200,
            render: (vendor) => (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {vendor.email || '-'}
              </Typography>
            ),
          },
          {
            key: 'phone_number',
            label: t('Phone Number'),
            render: (vendor) => (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {vendor.phone_number || '-'}
              </Typography>
            ),
          },
          {
            key: 'created_at',
            label: t('Created at'),
            sortable: true,
            width: '1%',
            noWrap: true,
            render: (vendor) => (
              <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>
                {formatDate(vendor.created_at)?.split(' ')[0]}
              </Typography>
            ),
          },
        ]}
        data={vendors}
        loading={loading}
        onRowClick={handleRowClick}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        emptyTitle={searchQuery ? t('vendors.noResults') : t('vendors.empty')}
        testIdPrefix="vendor-row"
        tableTestId="vendors-table"
        pagination={{
          page,
          totalPages,
          totalCount,
          pageSize,
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
        }}
      />

      {/* Mobile Cards */}
      <Box sx={{ display: { xs: 'block', lg: 'none' }, position: 'relative' }}>
        {/* Loading overlay */}
        {loading && vendors.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              pt: 8,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        {loading && vendors.length === 0 ? (
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : vendors.length === 0 ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {searchQuery ? t('vendors.noResults') : t('vendors.empty')}
            </Typography>
          </Paper>
        ) : (
          <>
            {vendors.map((vendor) => (
              <Paper
                key={vendor.id}
                data-testid={`vendor-row-${vendor.id}`}
                elevation={0}
                onClick={() => handleRowClick(vendor)}
                sx={{
                  p: 2,
                  mb: 1.5,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '15px',
                        fontWeight: 600,
                        color: 'text.primary',
                        mb: 0.5,
                      }}
                    >
                      {vendor.name}
                    </Typography>

                    {vendor.business_id && (
                      <Typography sx={{ fontSize: '12px', color: 'text.disabled', mb: 0.5 }}>
                        {vendor.business_id}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {vendor.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EmailIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                          <Typography
                            sx={{
                              fontSize: '13px',
                              color: 'text.secondary',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {vendor.email}
                          </Typography>
                        </Box>
                      )}
                      {vendor.phone_number && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                          <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                            {vendor.phone_number}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Paper>
            ))}

            <PaginationControls
              page={page}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              loading={loading}
            />
          </>
        )}
      </Box>

      {/* Add Vendor Dialog */}
      <VendorDialog
        open={addDialogOpen}
        handleClose={() => setAddDialogOpen(false)}
        vendor={null}
        onCreated={handleVendorCreated}
      />

      {/* Edit Vendor Dialog */}
      <VendorDialog
        open={editDialogOpen}
        handleClose={() => setEditDialogOpen(false)}
        vendor={selectedVendor}
        onUpdated={handleVendorUpdated}
        onDeleted={handleVendorDeleted}
      />
    </Box>
  );
};

export default withTranslation()(MainVendorsView);
